/**
 * Lifecycle commands and their idempotency keys.
 *
 * One Idempotency-Key per user INTENT: minted when the author starts an action
 * (opens a publish confirmation, presses Save draft, chooses a default) and
 * reused for every retry of that same action, including the retry the author
 * causes by pressing the button again after a network failure. A key is retired
 * once the server has answered (success or a definite refusal), so the next
 * action is a new intent with a new key. The request a key was minted for is
 * fingerprinted: if the author changes what they are asking for, it is a new
 * intent, never a replay with different content (the server would answer
 * IDEMPOTENCY_KEY_CONFLICT).
 */
import { newIdempotencyKey } from "@v0y4ge/api-client";
import { classifyFailure, type Failure } from "./api";

interface Held { key: string; fingerprint: string }
const held = new Map<string, Held>();

function fingerprint(request: unknown): string {
  try { return JSON.stringify(request); } catch { return String(Math.random()); }
}

/** The key for this intent; the same key while the same request is retried. */
export function intentKey(intent: string, request: unknown): string {
  const fp = fingerprint(request);
  const cur = held.get(intent);
  if (cur && cur.fingerprint === fp) return cur.key;
  const next = { key: newIdempotencyKey(), fingerprint: fp };
  held.set(intent, next);
  return next.key;
}

/** Forget the key once the server answered definitively. */
export function retireIntent(intent: string): void { held.delete(intent); }

export type CommandResult<T> = { ok: true; value: T } | { ok: false; failure: Failure };

/**
 * Whether the outcome of a failed call is unknown: no answer (transport, status
 * 0), a warming service, or a server error (5xx), any of which may come after the
 * write was applied. The key is kept so a retry REPLAYS the stored receipt rather
 * than applying the change a second time.
 */
export function outcomeUnknown(f: Failure): boolean {
  return f.kind === "transient" || f.status === 0 || f.status >= 500;
}

/**
 * Run one intent. An unknown outcome keeps the key, so the author's next press
 * of the same button is a replay the server recognises. A definite answer
 * (success, or a 4xx refusal) retires it.
 */
export async function runIntent<T>(intent: string, request: unknown, call: (idempotencyKey: string) => Promise<T>): Promise<CommandResult<T>> {
  const key = intentKey(intent, request);
  try {
    const value = await call(key);
    retireIntent(intent);
    return { ok: true, value };
  } catch (error) {
    const failure = classifyFailure(error);
    if (!outcomeUnknown(failure)) retireIntent(intent);
    return { ok: false, failure };
  }
}
